import {
  createUrl,
  createURLWithTokenAndCustomPath,
  // restDelete,
  restGet,
  // restPatch,
  restPost,
} from '~/src/dash/api/rest';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { Order } from '~/src/dash/components/tables/ListViewTable/common';

export enum InventoryRuleType {
  UNKNOWN = 0,
  VMAP = 1,
  VAST = 2,
  ORTB = 3,
}

export enum InventoryRuleResponseType {
  UNKNOWN = 0,
  INLINE_VAST = 1,
  AD_TAG_URI = 2,
}

export enum CuePointType {
  UNKNOWN = 0,
  FIXED = 1,
  PERCENTAGE = 2,
  PRE_POST_ROLL = 3,
}

export const inventoryRuleResponseTypeOptions: ISelectOption[] = [
  // { id: InventoryRuleResponseType.UNKNOWN, label: 'Unknown', tKey: 'Unknown' }, // FOR REFERENCE ONLY
  { id: InventoryRuleResponseType.INLINE_VAST, label: 'Inline VAST', tKey: 'InlineVAST' },
  { id: InventoryRuleResponseType.AD_TAG_URI, label: 'Ad Tag URI', tKey: 'AdTagURI' },
];

export enum InventoryRuleTimezoneType {
  UNKNOWN = 0,
  VIEWER = 1,
  PUBLISHER = 2,
}

export interface IInventoryRuleAdBreak {
  AdBreakID: string;
  PodDuration: number;
  SlotCount: number;
  MinAdDuration: number;
  MaxAdDuration: number;
  CuePointType: CuePointType;
  CuePointValue: string;
}

export interface IInventoryRuleAdbreaksSchedule {
  ID: number;
  Name: string;
  AdBreaks: IInventoryRuleAdBreak[];
}

export interface IInventoryRuleAdBreaksData {
  ID: number;
  Name: string;
  AssociatedInventoryRules: number;
  InventoryRules: unknown;
}

export interface IInventoryRuleContentData {
  ID: number;
  Name: string;
  ContentID: string;
  ContentCategory: string;
  ContentChannel: string;
  ContentContext: string;
  ContentDistributionID: string;
  ContentDistributionName: string;
  ContentEpisode: string;
  ContentGenre: string;
  ContentKeywords: string;
  ContentLanguage: string;
  ContentLength: number;
  ContentNetwork: string;
  ContentProdQuality: string;
  ContentProducerID: string;
  ContentProducer: string;
  ContentRating: string;
  ContentSeason: string;
  ContentSeries: string;
  ContentTaxonomy: string;
  ContentTitle: string;
  ContentURL: string;
  AssociatedInventoryRules: number;
  InventoryRules: unknown;
}

export interface IInventoryRuleCustomData {
  ID: number;
  Name: string;
  Custom5: string;
  Custom6: string;
  Custom7: string;
  Custom8: string;
  Custom9: string;
  Custom10: string;
  Custom11: string;
  Custom12: string;
  Custom13: string;
  Custom14: string;
  Custom15: string;
  Custom16: string;
  Custom17: string;
  Custom18: string;
  AssociatedInventoryRules: number;
  InventoryRules: unknown;
}

export interface IInventoryRuleDataRef {
  ID: number;
  Name: string;
}

export interface IInventoryRuleContentFields {
  ContentID: string;
  ContentCategory: string;
  ContentChannel: string;
  ContentContext: string;
  ContentDistributionID: string;
  ContentDistributionName: string;
  ContentEpisode: string;
  ContentGenre: string;
  ContentKeywords: string;
  ContentLanguage: string;
  ContentLength: number;
  ContentNetwork: string;
  ContentProdQuality: string;
  ContentProducerID: string;
  ContentProducer: string;
  ContentRating: string;
  ContentSeason: string;
  ContentSeries: string;
  ContentTaxonomy: string;
  ContentTitle: string;
  ContentURL: string;
}

export interface IInventoryRuleCustomFields {
  Custom5: string;
  Custom6: string;
  Custom7: string;
  Custom8: string;
  Custom9: string;
  Custom10: string;
  Custom11: string;
  Custom12: string;
  Custom13: string;
  Custom14: string;
  Custom15: string;
  Custom16: string;
  Custom17: string;
  Custom18: string;
}

export interface IInventoryRule extends IInventoryRuleContentFields, IInventoryRuleCustomFields {
  ID: number;
  Name: string;
  TimezoneType: InventoryRuleTimezoneType;
  LookupKey: string;
  LookupValue: string;
  RuleType: InventoryRuleType;
  ResponseType: InventoryRuleResponseType;
  StartDateTime: string;
  EndDateTime: string;
  AdBreaks: IInventoryRuleAdBreak[];
  ContentData: IInventoryRuleDataRef | null;
  CustomsData: IInventoryRuleDataRef | null;
  AdBreaksData: IInventoryRuleDataRef | null;
}

// Shape used by getSingleInventoryRule and the save/update API.
// Unlike IInventoryRule (list shape), content/custom/adbreak fields are
// nested inside their respective data objects rather than flat at the root.
export interface IInventoryRuleDetail {
  ID: number;
  Name: string;
  TimezoneType: InventoryRuleTimezoneType;
  LookupKey: string;
  LookupValue: string;
  RuleType: InventoryRuleType;
  ResponseType: InventoryRuleResponseType;
  StartDateTime: string;
  EndDateTime: string;
  ContentData: (IInventoryRuleDataRef & IInventoryRuleContentFields) | null;
  CustomsData: (IInventoryRuleDataRef & IInventoryRuleCustomFields) | null;
  AdBreaksData: (IInventoryRuleDataRef & { AdBreaks: IInventoryRuleAdBreak[] }) | null;
}

export interface IGetInventoryRulesData {
  InventoryRules: IInventoryRule[];
  TotalCount: number;
}

export interface IInventoryRulesQueryParams {
  Search?: string;
  LookupKey?: string;
  StartDateFrom?: string;
  StartDateTo?: string;
  EndDateFrom?: string;
  EndDateTo?: string;
  RuleType?: number;
  ContentGenre?: string[];
  ContentTitle?: string[];
  ContentNetwork?: string[];
  ContentCategory?: string[];
  ContentKeywords?: string[];
  Page: number;
  PageSize: number;
  OrderBy: string;
  OrderDirection: Order;
}

export async function getInventoryRulesData(
  publisherID: string,
  query: IInventoryRulesQueryParams,
  signal?: AbortSignal,
): Promise<IGetInventoryRulesData> {
  const reqUrl = `${createUrl('/inventory_rules_v2/search', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query, undefined, undefined, signal);
  return (
    (response?.parsedBody as IGetInventoryRulesData) || {
      InventoryRules: [],
      TotalCount: 0,
    }
  );
}

const contentFields = ['ContentCategory', 'ContentGenre', 'ContentNetwork', 'ContentTitle'];

const inventoryRuleFields = ['LookupKey'];

export async function getInventoryRulesFilters(
  publisherID: string,
): Promise<Record<string, ISelectOption[]>> {
  const reqUrl = `${createURLWithTokenAndCustomPath(
    '/uimd',
  )}&publisher_id=${publisherID}&flatten=true&model=inv_rule_content_data:${contentFields.join(
    ',',
  )}%7Cinventory_rule:${inventoryRuleFields.join(',')}`;
  const response = await restGet<unknown>(reqUrl, {});
  return (
    (response?.parsedBody as Record<string, ISelectOption[]>) || {
      LookupKey: [],
    }
  );
}

export interface IInventoryRulesPublisherSettings {
  TimezoneType: InventoryRuleTimezoneType;
}

export async function getInventoryRulesPublisherSettings(
  publisherID: string,
): Promise<InventoryRuleTimezoneType> {
  const reqUrl = `${createUrl('/inv_rule_publisher_setting', publisherID, 'v1')}`;
  const response = await restGet<unknown>(reqUrl, {});
  return (
    (response?.parsedBody as IInventoryRulesPublisherSettings).TimezoneType ||
    InventoryRuleTimezoneType.UNKNOWN
  );
}

export async function updateInventoryRulesPublisherSettings(
  publisherID: string,
  timezoneType: InventoryRuleTimezoneType,
  signal?: AbortSignal,
): Promise<InventoryRuleTimezoneType> {
  const reqUrl = `${createUrl('/inv_rule_publisher_setting', publisherID, 'v1')}`;
  const response = await restPost<unknown>(
    reqUrl,
    { TimezoneType: timezoneType },
    undefined,
    undefined,
    signal,
  );
  return (
    (response?.parsedBody as IInventoryRulesPublisherSettings).TimezoneType ||
    InventoryRuleTimezoneType.UNKNOWN
  );
}

const noneOption: ISelectOption = { id: 0, label: 'None', tKey: 'None' };

const normalizeOptionIDs = (options: ISelectOption[]): ISelectOption[] =>
  options.map((o) => ({ ...o, id: Number(o.id) }));

const prependNoneOption = (options: ISelectOption[]): ISelectOption[] => [
  noneOption,
  ...normalizeOptionIDs(options),
];

export async function getInventoryRulesFormOptions(
  publisherID: string,
): Promise<Record<string, ISelectOption[]>> {
  const reqUrl = `${createURLWithTokenAndCustomPath(
    '/settings/inv_rule_data_options',
  )}&publisher_id=${publisherID}`;
  const response = await restGet<unknown>(reqUrl, {});
  const parsed = response?.parsedBody as Record<string, ISelectOption[]> | undefined;
  return {
    AdbreaksSchedules: prependNoneOption(parsed?.AdbreaksSchedules ?? []),
    Content: prependNoneOption(parsed?.Content ?? []),
    Customs: prependNoneOption(parsed?.Customs ?? []),
  };
}

export async function getInventoryRuleAdBreakData(
  dataID: string,
  publisherID: string,
): Promise<IInventoryRuleAdbreaksSchedule> {
  const reqUrl = `${createURLWithTokenAndCustomPath(
    `/settings/inv_rule_adbreaks_data/${dataID}`,
  )}&publisher_id=${publisherID}`;
  const response = await restGet<unknown>(reqUrl, {});
  return response?.parsedBody as IInventoryRuleAdbreaksSchedule;
}

export async function getInventoryRuleContentData(
  dataID: string,
  publisherID: string,
): Promise<IInventoryRuleContentData> {
  const reqUrl = `${createURLWithTokenAndCustomPath(
    `/settings/inv_rule_content_data/${dataID}`,
  )}&publisher_id=${publisherID}`;
  const response = await restGet<unknown>(reqUrl, {});
  return response?.parsedBody as IInventoryRuleContentData;
}

export async function getInventoryRuleCustomData(
  dataID: string,
  publisherID: string,
): Promise<IInventoryRuleCustomData> {
  const reqUrl = `${createURLWithTokenAndCustomPath(
    `/settings/inv_rule_customs_data/${dataID}`,
  )}&publisher_id=${publisherID}`;
  const response = await restGet<unknown>(reqUrl, {});
  return response?.parsedBody as IInventoryRuleCustomData;
}

// export async function getSingleInventoryRule(
//   dataID: string,
//   publisherID: string,
// ): Promise<IInventoryRuleDetail> {
//   const reqUrl = `${createURLWithTokenAndCustomPath(
//     `/settings/inventory_rules_v2/${dataID}`,
//   )}&publisher_id=${publisherID}`;
//   const response = await restGet<unknown>(reqUrl, {});
//   return response?.parsedBody as IInventoryRuleDetail;
// }

// export async function patchInventoryRule(
//   dataID: string,
//   publisherID: string,
//   payload: IInventoryRuleDetail,
// ): Promise<IInventoryRuleDetail> {
//   const reqUrl = `${createURLWithTokenAndCustomPath(
//     `/settings/inventory_rules_v2/${dataID}`,
//   )}&publisher_id=${publisherID}`;
//   const response = await restPatch<unknown>(reqUrl, payload);
//   return response?.parsedBody as IInventoryRuleDetail;
// }

// export async function createInventoryRule(
//   publisherID: string,
//   payload: IInventoryRuleDetail,
// ): Promise<IInventoryRuleDetail> {
//   const reqUrl = `${createURLWithTokenAndCustomPath(
//     '/settings/inventory_rules_v2',
//   )}&publisher_id=${publisherID}`;
//   const response = await restPost<unknown>(reqUrl, payload);
//   return response?.parsedBody as IInventoryRuleDetail;
// }

// export async function deleteInventoryRule(ruleID: string, publisherID: string): Promise<boolean> {
//   const reqUrl = `${createURLWithTokenAndCustomPath(
//     `/settings/inventory_rules_v2/${ruleID}`,
//   )}&publisher_id=${publisherID}`;
//   try {
//     await restDelete<{ success: boolean }>(reqUrl, undefined);
//     return true;
//   } catch (e) {
//     throw e;
//   }
// }
